import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

export interface ModelIdentifier {
  name: string;
  model_description: string;
  slug?: string;
  is_selected?: boolean;
  cost_per_message?: number;
  tier?: 'free' | 'cheap' | 'standard' | 'premium' | 'ultra';
}

interface InferenceSettings {
  maxTokens: number;
  temperature: number;
  topP: number;
  presencePenalty: number;
  frequencyPenalty: number;
}

interface Customization {
  user_color: string;
  char_color: string;
  userTextColor: string;
  charTextColor: string;
  userItalicColor: string;
  charItalicColor: string;
  userQuotedColor: string;
  charQuotedColor: string;
}

interface TypingIndicatorSettings {
  symbol: string;
  speed: number;
  repeat: number;
  dotSize: number;
  dotColor: string;
  animationDuration: number;
}

interface CachedUserData {
  data: {
    country_name?: string;
    is_deleted?: boolean;
    is_banned?: boolean;
  };
  gotten_at: number;
}

interface UserStore {
  userUUID: string | null;
  username: string | null;
  personaName: string | null;
  userIcon: string | null;
  is_login: boolean;
  is_deleted: boolean | undefined;
  is_banned: boolean | undefined;
  is_admin: boolean;
  credits: number;
  totalCreditsPurchased: number;
  tier: string;
  imageURL: string | null;
  preferredModel: string;
  inferenceSettings: InferenceSettings;
  modelIdentifiers: ModelIdentifier[];
  maxTokenLimit: number;
  customization: Customization;
  typingIndicator: TypingIndicatorSettings;
  include_forbiddens: boolean;
  include_sensitive: boolean;
  date: string | null;
  pendingPurchase: { credits: number; amount: number } | null;
  cachedUserData?: CachedUserData;
  token: string | null;
  strip_incomplete_output: boolean;
  currentThemeId?: string;

  setUserUUID: (uuid: string) => void;
  setUsername: (name: string) => void;
  setPersonaName: (name: string) => void;
  setUserIcon: (icon: string) => void;
  setIsLogin: (isLogin: boolean) => void;
  setIsDeleted: (isDeleted: boolean | undefined) => void;
  setIsBanned: (isBanned: boolean | undefined) => void;
  setIsAdmin: (isAdmin: boolean) => void;
  setCredits: (credits: number) => void;
  addCredits: (amount: number) => void;
  deductCredits: (amount: number) => boolean;
  setTotalCreditsPurchased: (total: number) => void;
  setTier: (tier: string) => void;
  setImageURL: (url: string | null) => void;
  setPreferredModel: (model: string) => void;
  setInferenceSettings: (settings: InferenceSettings) => void;
  setModelIdentifiers: (models: ModelIdentifier[]) => void;
  setMaxTokenLimit: (limit: number) => void;
  setCustomization: (customization: Partial<Customization>) => void;
  setTypingIndicator: (settings: Partial<TypingIndicatorSettings>) => void;
  setIncludeForbiddens: (val: boolean) => void;
  setIncludeSensitive: (val: boolean) => void;
  setDate: (date: string | null) => void;
  enableNSFW: (date: string) => void;
  disableNSFW: () => void;
  setPendingPurchase: (purchase: { credits: number; amount: number } | null) => void;
  setCachedUserData: (cache: CachedUserData | undefined) => void;
  clearCachedUserData: () => void;
  setToken: (token: string | null) => void;
  setStripIncompleteOutput: (value: boolean) => void;
  setTheme: (themeId: string) => void;
  getEstimatedMessages: () => number;
  canAffordModel: (modelCost: number) => boolean;
  clearAllUserData: () => void;
}

export const useUserStore = create<UserStore>()(
  persist(
    (set, get) => ({
      userUUID: null,
      username: null,
      personaName: null,
      userIcon: null,
      is_login: false,
      is_deleted: undefined,
      is_banned: undefined,
      is_admin: false,
      credits: 0,
      totalCreditsPurchased: 0,
      tier: 'FREE',
      imageURL: null,
      preferredModel: '',
      inferenceSettings: {
        maxTokens: 100,
        temperature: 1,
        topP: 1,
        presencePenalty: 0,
        frequencyPenalty: 0,
      },
      modelIdentifiers: [],
      maxTokenLimit: 200,
      customization: {
        user_color: '#555555',
        char_color: 'rgba(20,24,28,0.6)',
        userTextColor: '#FFFFFF',
        charTextColor: '#FFFFFF',
        userItalicColor: '#999999',
        charItalicColor: '#999999',
        userQuotedColor: '#AAAAAA',
        charQuotedColor: '#93BEE6',
      },
      typingIndicator: {
        symbol: '.',
        speed: 250,
        repeat: 3,
        dotSize: 8,
        dotColor: '#333333',
        animationDuration: 1400,
      },
      include_forbiddens: false,
      include_sensitive: false,
      date: null,
      pendingPurchase: null,
      cachedUserData: undefined,
      token: null,
      strip_incomplete_output: true,
      currentThemeId: undefined,

      setUserUUID: (uuid) => set({ userUUID: uuid }),
      setUsername: (name) => set({ username: name }),
      setPersonaName: (name) => set({ personaName: name }),
      setUserIcon: (icon) => set({ userIcon: icon }),
      setIsLogin: (isLogin) => set({ is_login: isLogin }),
      setIsDeleted: (isDeleted) => set({ is_deleted: isDeleted }),
      setIsBanned: (isBanned) => set({ is_banned: isBanned }),
      setIsAdmin: (isAdmin) => set({ is_admin: isAdmin }),
      setCredits: (credits) => set({ credits }),
      addCredits: (amount) => set((state) => ({ 
        credits: state.credits + amount,
        totalCreditsPurchased: state.totalCreditsPurchased + amount
      })),
      deductCredits: (amount) => {
        const current = get().credits;
        if (current < amount) return false;
        set({ credits: current - amount });
        return true;
      },
      setTotalCreditsPurchased: (total) => set({ totalCreditsPurchased: total }),
      setTier: (tier) => set({ tier }),
      setImageURL: (url) => set({ imageURL: url }),
      setPreferredModel: (model) => set({ preferredModel: model }),
      setInferenceSettings: (settings) => set({ inferenceSettings: settings }),
      setModelIdentifiers: (models) => set({ modelIdentifiers: models }),
      setMaxTokenLimit: (limit) => set({ maxTokenLimit: limit }),
      setCustomization: (customization) =>
        set((state) => ({
          customization: { ...state.customization, ...customization },
        })),
      setTypingIndicator: (settings) =>
        set((state) => ({
          typingIndicator: { ...state.typingIndicator, ...settings },
        })),
      setIncludeForbiddens: (val) => set({ include_forbiddens: val }),
      setIncludeSensitive: (val) => set({ include_sensitive: val }),
      setDate: (date) => set({ date }),
      enableNSFW: (date) => set({ 
        include_sensitive: true, 
        date 
      }),
      disableNSFW: () => set({ 
        include_sensitive: false, 
        date: null 
      }),
      setPendingPurchase: (purchase) => set({ pendingPurchase: purchase }),
      setCachedUserData: (cache) => set({ cachedUserData: cache }),
      clearCachedUserData: () => set({ cachedUserData: undefined }),
      setToken: (token) => set({ token }),
      setStripIncompleteOutput: (value) =>
        set({ strip_incomplete_output: value }),
      setTheme: (themeId) => set({ currentThemeId: themeId }),
      getEstimatedMessages: () => {
        const credits = get().credits;
        return credits;
      },
      canAffordModel: (modelCost) => get().credits >= modelCost,
      clearAllUserData: () => set({
        userUUID: null,
        username: null,
        personaName: null,
        userIcon: null,
        is_login: false,
        is_deleted: undefined,
        is_banned: undefined,
        is_admin: false,
        credits: 0,
        totalCreditsPurchased: 0,
        tier: 'FREE',
        imageURL: null,
        preferredModel: '',
        modelIdentifiers: [],
        maxTokenLimit: 200,
        include_forbiddens: false,
        include_sensitive: false,
        date: null,
        pendingPurchase: null,
        cachedUserData: undefined,
        token: null,
        strip_incomplete_output: true,
        currentThemeId: undefined,
      }),
    }),
    {
      name: 'user-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        customization: state.customization,
        typingIndicator: state.typingIndicator,
        preferredModel: state.preferredModel,
        inferenceSettings: state.inferenceSettings,
        maxTokenLimit: state.maxTokenLimit,
        strip_incomplete_output: state.strip_incomplete_output,
        currentThemeId: state.currentThemeId,
      }),
    }
  )
);